import { KIND_LABELS, type Task, type TaskComment, type TaskPr } from '@shared/domain.ts';

/**
 * The whole task, as one paste-ready document.
 *
 * `buildBrief` (server side) is instructions handed to a routine that has not
 * started yet; this is the opposite direction — a snapshot of a task that is
 * already under way, for handing to a person or another session that was not
 * in the room. So it carries what a routine's brief does not: the comment
 * thread, the screenshots, and where things actually stand right now.
 *
 * Kept as a pure function of plain data — no hooks, no `Date.now()` — so it
 * takes exactly what a caller already has in hand and stays trivial to test.
 */
export interface TaskMarkdownInput {
  task: Task;
  /** Origin the task lives at, e.g. `https://rigg.example.com` — no trailing slash. */
  appUrl: string;
  projectName: string | null;
  repoLabel: string | null;
  categoryLabel: string | null;
  /** Where it came from, as the board says it — see `originText`. */
  originLabel: string;
  /** Every pull request this task carries, sorted by number. */
  prs: TaskPr[];
  /** As many comments as are loaded, oldest first. */
  comments: TaskComment[];
  /** May exceed `comments.length` if older ones have not been paged in. */
  totalComments: number;
  authorNameOf: (comment: TaskComment) => string;
  /**
   * "Where things stand" — `task.status_note` when there is one, otherwise
   * whatever the panel is showing in its place. Passed in rather than
   * recomputed here because that fallback text depends on the clock and on
   * live agent-pulse state this function has no business knowing about.
   */
  standing: string;
  /** The full instruction brief, when it was fetched — omitted if not. */
  brief?: string | null;
}

function timestamp(iso: string): string {
  // Trimmed to the minute and kept absolute: this document outlives "3h ago".
  return iso.replace(/:\d{2}(\.\d+)?Z$/, 'Z');
}

export function buildTaskMarkdown(input: TaskMarkdownInput): string {
  const { task } = input;
  const lines: string[] = [];

  lines.push(`# ${task.title}`);
  lines.push('');

  const where =
    `Task #${task.ref}` +
    (input.projectName ? ` in the **${input.projectName}** project` : '') +
    (input.repoLabel ? `, on \`${input.repoLabel}\`` : '') +
    (input.categoryLabel ? ` · ${input.categoryLabel}` : '') +
    '.';
  lines.push(where);
  lines.push(`Kind: ${task.kind ? KIND_LABELS[task.kind] : 'not set'} · Origin: ${input.originLabel}`);
  lines.push(`Rigg: ${input.appUrl}/t/${task.id}`);
  lines.push('');

  // Verbatim, as a quote: the requester's own words, not a brief.
  if (task.quote) {
    lines.push('## What they said');
    lines.push('');
    for (const line of task.quote.trim().split('\n')) lines.push(line ? `> ${line}` : '>');
    lines.push('');
  }

  lines.push('## Where things stand');
  lines.push('');
  lines.push(`Status: **${task.status}**${task.auto_merge ? ' · auto-merge on' : ''}`);
  lines.push('');
  lines.push(input.standing);
  lines.push('');

  lines.push('## What was asked');
  lines.push('');
  lines.push(task.prompt.trim());
  lines.push('');

  lines.push('## Branch and pull requests');
  lines.push('');
  lines.push(`- Branch: ${task.branch ? `\`${task.branch}\`` : 'none yet'}`);
  if (input.prs.length > 0) {
    for (const pr of input.prs) {
      lines.push(`- Pull request #${pr.number} (${pr.state}): ${pr.url}`);
    }
  } else if (task.pr_url) {
    lines.push(`- Pull request${task.pr_number ? ` #${task.pr_number}` : ''} (${task.pr_state ?? 'open'}): ${task.pr_url}`);
  } else {
    lines.push('- No pull request yet.');
  }
  lines.push('');

  if (task.images.length > 0) {
    lines.push('## Screenshots');
    lines.push('');
    for (const image of task.images) {
      lines.push(`- ${image.label ? `${image.label}: ` : ''}${image.url}`);
    }
    lines.push('');
  }

  if (task.links.filter((link) => link.kind !== 'pr').length > 0) {
    lines.push('## Links');
    lines.push('');
    for (const link of task.links) {
      if (link.kind === 'pr') continue;
      lines.push(`- ${link.label}: ${link.url}`);
    }
    lines.push('');
  }

  lines.push('## Comments');
  lines.push('');
  if (input.comments.length === 0) {
    lines.push('No comments yet.');
  } else {
    const skipped = input.totalComments - input.comments.length;
    if (skipped > 0) {
      lines.push(`*(${skipped} earlier comment${skipped === 1 ? '' : 's'} not loaded)*`);
      lines.push('');
    }
    for (const comment of input.comments) {
      const author = input.authorNameOf(comment);
      const from = comment.origin === 'github' ? ' · from GitHub' : '';
      lines.push(`**${author}** — ${timestamp(comment.created_at)}${from}`);
      lines.push('');
      lines.push(comment.body.trim());
      lines.push('');
    }
  }

  if (input.brief) {
    lines.push('## Brief given to the routine');
    lines.push('');
    lines.push(input.brief.trim());
    lines.push('');
  }

  return lines.join('\n').trimEnd() + '\n';
}
